import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../config/environment.config";

/**
 * Chevauchements horaires d'un même prof : un prof ne donne pas deux cours
 * au même moment, quel que soit l'élève. Ce module ne contient que les codes
 * d'erreur du serveur (TeachrScheduleConflictChecker) et les messages
 * affichés : il est importé aussi bien par le formulaire de séances que par
 * la gestion des erreurs, sans dépendance vers l'un ou l'autre.
 */
export const OVERLAP_ERROR_CODE = "course_session_overlap";
export const INTERNAL_OVERLAP_ERROR_CODE = "course_sessions_internal_overlap";
/** Le serveur encode le détail du conflit après le code : `code|détail|…`. */
export const OVERLAP_SEPARATOR = "|";

export interface InternalOverlap {
    /** Numéros de séance, à partir de 1, dans l'ordre d'affichage. */
    first: number;
    second: number;
    firstSlot: string;
    secondSlot: string;
}

export const describeInternalOverlap = (overlap: InternalOverlap): string =>
    `Les séances n°${overlap.first} (${overlap.firstSlot}) et n°${overlap.second} (${overlap.secondSlot}) se chevauchent. Un même créneau ne peut pas accueillir deux séances : modifiez l'heure ou le jour de l'une d'elles.`;

/**
 * Conflit avec un autre cours du prof. `which` désigne la séance en cause
 * (« La séance n°2 (…) ») ; sans lui, le message parle du créneau.
 */
export const describeConflictWithOtherCourse = (slot: string, existingSlot: string, studentName: string, which?: string): string => {
    const subject = which ?? `Le créneau ${slot}`;
    const withWhom = studentName ? ` avec ${studentName}` : "";
    return `${subject} chevauche ${getActivePrestationBrandConfig("", "un ", "une ")} déjà prévu${withWhom}, le ${existingSlot}. Vous ne pouvez pas donner deux ${getActivePrestationBrandConfig("s")} au même moment, même à deux ${getActiveClientBrandConfig("s")} différents : modifiez l'heure ou le jour.`;
};

/**
 * Traduit un code d'erreur du serveur (`code|détail|…`) en message lisible,
 * ou null si ce n'est pas une erreur de chevauchement.
 */
export const describeOverlapApiError = (description: unknown): string | null => {
    if (typeof description !== "string") return null;
    const parts = description.split(OVERLAP_SEPARATOR);
    if (parts[0] === INTERNAL_OVERLAP_ERROR_CODE) {
        const [, first, second, firstSlot, secondSlot] = parts;
        return describeInternalOverlap({ first: Number(first), second: Number(second), firstSlot: firstSlot ?? "", secondSlot: secondSlot ?? "" });
    }
    if (parts[0] === OVERLAP_ERROR_CODE) {
        const [, slot, existingSlot, studentName] = parts;
        return describeConflictWithOtherCourse(slot ?? "", existingSlot ?? "", studentName ?? "");
    }
    return null;
};
